import { ReactNode, useState } from 'react';
import { useLocation, Link } from 'wouter';
import { Music, Users, ListMusic, Library, FileText, Settings, Menu, X, Home, ChevronLeft, LogOut, MessageSquare } from 'lucide-react';
import { useAuth } from '../hooks/use-auth';
import { SyncIndicator } from './SyncIndicator';

interface LayoutProps {
  children: ReactNode;
  bandId?: number;
  bandName?: string;
  title?: string;
  showBack?: boolean;
  backTo?: string;
}

export function Layout({ children, bandId, bandName, title, showBack, backTo }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [location] = useLocation();
  const { user, logout } = useAuth();

  const displayName = user ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'User' : '';

  const bandNav = bandId ? [
    { href: `/bands/${bandId}`, icon: Home, label: 'Home' },
    { href: `/bands/${bandId}/songs`, icon: Library, label: 'Song Library' },
    { href: `/bands/${bandId}/setlists`, icon: ListMusic, label: 'Setlists' },
    { href: `/bands/${bandId}/notes`, icon: MessageSquare, label: 'Rehearsal Notes' },
    { href: `/bands/${bandId}/settings`, icon: Settings, label: 'Settings' },
  ] : [];

  const globalNav = [
    { href: '/', icon: Users, label: 'My Bands' },
    { href: '/my-charts', icon: FileText, label: 'My Charts' },
  ];

  const navItems = bandId ? bandNav : globalNav;

  return (
    <div className="h-full flex flex-col bg-surface-950">
      <header className="bg-surface-900 border-b border-surface-700 px-4 py-3 flex items-center gap-3 shrink-0 z-20">
        {showBack && backTo ? (
          <Link href={backTo}>
            <button className="p-1 hover:bg-surface-800 rounded-lg transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
          </Link>
        ) : (
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1 hover:bg-surface-800 rounded-lg transition-colors lg:hidden">
            <Menu className="w-5 h-5" />
          </button>
        )}
        <Music className="w-6 h-6 text-primary-400" />
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold truncate">{title || bandName || 'Chartz'}</h1>
          {bandName && title && <p className="text-xs text-gray-400 truncate">{bandName}</p>}
        </div>
        <SyncIndicator />
        {user && (
          <div className="flex items-center gap-2">
            {user.profileImageUrl ? (
              <img src={user.profileImageUrl} alt="" className="w-7 h-7 rounded-full" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center text-xs font-bold">
                {displayName[0]?.toUpperCase() || '?'}
              </div>
            )}
            <button onClick={() => logout()} className="p-1 hover:bg-surface-800 rounded-lg transition-colors" title="Sign out">
              <LogOut className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        )}
      </header>

      <div className="flex-1 flex overflow-hidden">
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        <nav className={`fixed lg:static inset-y-0 left-0 z-40 w-60 bg-surface-900 border-r border-surface-700 transform transition-transform lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} flex flex-col pt-4`}>
          <div className="flex items-center justify-between px-4 mb-4 lg:hidden">
            <span className="font-semibold text-sm text-gray-400">Menu</span>
            <button onClick={() => setSidebarOpen(false)}><X className="w-5 h-5" /></button>
          </div>
          {bandName && (
            <div className="px-4 mb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Band</p>
              <p className="text-sm font-semibold text-primary-300 truncate">{bandName}</p>
            </div>
          )}
          <div className="flex-1 space-y-1 px-2">
            {navItems.map(item => {
              const isActive = location === item.href;
              return (
                <Link key={item.href} href={item.href} onClick={() => setSidebarOpen(false)}>
                  <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${isActive ? 'bg-primary-600/20 text-primary-300' : 'text-gray-400 hover:bg-surface-800 hover:text-gray-200'}`}>
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </div>
                </Link>
              );
            })}
          </div>
          {bandId && (
            <div className="border-t border-surface-700 p-2 space-y-1">
              {globalNav.map(item => (
                <Link key={item.href} href={item.href} onClick={() => setSidebarOpen(false)}>
                  <div className="flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-gray-500 hover:bg-surface-800 hover:text-gray-300 cursor-pointer transition-colors">
                    <item.icon className="w-3.5 h-3.5" />
                    {item.label}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </nav>

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
